import React, { useState } from 'react';
import { X, Building, User, Calendar, Phone, Mail, MessageSquare, Linkedin, Globe, Edit, Save, Upload } from 'lucide-react';

interface LeadDetailsModalProps {
  lead: any;
  onClose: () => void;
  onUpdateLead: (rowIndex: number, columnName: string, value: string) => Promise<void>;
}
import { PIPELINE_STAGE_NAMES } from '../../shared/constants';

const LeadDetailsModal: React.FC<LeadDetailsModalProps> = ({ lead, onClose, onUpdateLead }) => {
  console.log('[LeadDetailsModal] Received lead prop:', {
    channel: lead.channel,
    last_reply_timestamp: lead.last_reply_timestamp,
    next_fu_datetime: lead.next_fu_datetime,
    company_name: lead.company_name,
    dm_full_name: lead.dm_full_name
  });

  const [selectedStage, setSelectedStage] = useState(lead.pipeline_stage || lead.stage || '');
  const [notes, setNotes] = useState(lead.notes || '');
  const [isUpdatingStage, setIsUpdatingStage] = useState(false);
  const [isUpdatingNotes, setIsUpdatingNotes] = useState(false);

  console.log('[LeadDetailsModal] Lead data:', lead);
  console.log('[LeadDetailsModal] Channel value:', lead.channel);
  console.log('[LeadDetailsModal] Last reply timestamp:', lead.last_reply_timestamp);
  console.log('[LeadDetailsModal] Next follow up:', lead.next_fu_datetime);

  const getChannelTag = (channel: string) => {
    console.log('[LeadDetailsModal] getChannelTag input channel:', channel, 'type:', typeof channel);
    console.log('[LeadDetailsModal] Processing channel:', channel);
    
    if (!channel || channel.toString().trim() === '' || channel === 'undefined' || channel === 'null') {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800 border border-gray-200">
          <MessageSquare size={12} className="mr-1" />
          Unknown
        </span>
      );
    }

    const normalizedChannel = channel.toString().toLowerCase().trim();
    console.log('[LeadDetailsModal] Normalized channel:', normalizedChannel);
    
    switch (normalizedChannel) {
      case 'email':
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800 border border-red-200">
            <Mail size={12} className="mr-1" />
            Email
          </span>
        );
      case 'call':
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 border border-blue-200">
            <Phone size={12} className="mr-1" />
            Call
          </span>
        );
      case 'whatsapp':
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 border border-green-200">
            <MessageSquare size={12} className="mr-1" />
            WhatsApp
          </span>
        );
      case 'linkedin':
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 border border-blue-200">
            <Linkedin size={12} className="mr-1" />
            LinkedIn
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800 border border-gray-200">
            <MessageSquare size={12} className="mr-1" />
            {channel}
          </span>
        );
    }
  };

  const formatDateTime = (timestamp: string) => {
    console.log('[LeadDetailsModal] formatDateTime input:', timestamp, 'type:', typeof timestamp);
    if (!timestamp || timestamp === 'N/A' || timestamp.toString().trim() === '' || timestamp === 'undefined' || timestamp === 'null') return 'N/A';
    
    try {
      const date = new Date(timestamp);
      if (isNaN(date.getTime())) return 'N/A';
      const result = date.toLocaleString();
      console.log('[LeadDetailsModal] formatDateTime result:', result);
      return result;
    } catch {
      return 'N/A';
    }
  };

  const formatTimeAgo = (timestamp: string) => {
    console.log('[LeadDetailsModal] formatTimeAgo input:', timestamp, 'type:', typeof timestamp);
    if (!timestamp || timestamp === 'N/A' || timestamp.toString().trim() === '' || timestamp === 'undefined' || timestamp === 'null') return null;
    
    try {
      const date = new Date(timestamp);
      if (isNaN(date.getTime())) return null;
      
      const now = new Date();
      const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));
      
      let result;
      if (diffInHours < 1) {
        result = 'Just now';
      } else if (diffInHours < 24) {
        result = `${diffInHours}h ago`;
      } else {
        const diffInDays = Math.floor(diffInHours / 24);
        result = `${diffInDays}d ago`;
      }
      
      console.log('[LeadDetailsModal] formatTimeAgo result:', result);
      return result;
    } catch {
      return null;
    }
  };

  const formatNextFollowUp = (timestamp: string) => {
    console.log('[LeadDetailsModal] formatNextFollowUp input:', timestamp, 'type:', typeof timestamp);
    if (!timestamp || timestamp === 'N/A' || timestamp.toString().trim() === '' || timestamp === 'undefined' || timestamp === 'null') return null;
    
    try {
      const date = new Date(timestamp);
      if (isNaN(date.getTime())) return null;
      
      const now = new Date();
      const diffInHours = (date.getTime() - now.getTime()) / (1000 * 60 * 60);
      
      let result;
      if (diffInHours < 0) {
        result = 'Overdue';
      } else if (diffInHours < 24) {
        result = `In ${Math.ceil(diffInHours)}h`;
      } else {
        const diffInDays = Math.ceil(diffInHours / 24);
        result = `In ${diffInDays}d`;
      }
      
      console.log('[LeadDetailsModal] formatNextFollowUp result:', result);
      return result;
    } catch {
      return null;
    }
  };

  const handleStageChange = async (newStage: string) => {
    if (!lead._rowIndex || newStage === selectedStage) return;
    
    console.log('[LeadDetailsModal] Updating stage:', { rowIndex: lead._rowIndex, newStage });
    
    setIsUpdatingStage(true);
    try {
      await onUpdateLead(lead._rowIndex, 'pipeline_stage', newStage);
      setSelectedStage(newStage);
      console.log('[LeadDetailsModal] Stage updated successfully');
    } catch (error) {
      console.error('[LeadDetailsModal] Failed to update stage:', error);
    } finally {
      setIsUpdatingStage(false);
    }
  };

  const handleSaveNotes = async () => {
    if (!lead._rowIndex) {
      console.error('[LeadDetailsModal] No row index available for notes update');
      return;
    }
    
    console.log('[LeadDetailsModal] Saving notes:', { rowIndex: lead._rowIndex, notes });
    
    setIsUpdatingNotes(true);
    try {
      await onUpdateLead(lead._rowIndex, 'notes', notes);
      console.log('[LeadDetailsModal] Notes saved successfully');
    } catch (error) {
      console.error('[LeadDetailsModal] Failed to save notes:', error);
    } finally {
      setIsUpdatingNotes(false);
    }
  };

  const handlePushToCRM = () => {
    alert('Push to CRM functionality will be implemented soon!');
  };

  const lastActivity = formatTimeAgo(lead.last_reply_timestamp);
  const nextFollowUp = formatNextFollowUp(lead.next_fu_datetime);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex">
      {/* Backdrop - click to close */}
      <div className="flex-1" onClick={onClose}></div>
      
      {/* Right-side panel - Full height */}
      <div className="w-96 bg-white h-full max-h-screen shadow-xl transform transition-transform duration-300 ease-in-out flex flex-col">
        {/* Header - Fixed */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gray-50 flex-shrink-0">
          <h3 className="text-lg font-semibold text-gray-900">Lead Details</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto p-4 space-y-6">
          {/* Contact Information */}
          <div>
            <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center">
              <User size={16} className="mr-2 text-blue-600" />
              Contact Information
            </h4>
            <div className="space-y-3 bg-gray-50 p-3 rounded-lg">
              {lead.deal_id && (
                <div>
                  <span className="text-xs text-gray-500 block">Deal ID</span>
                  <span className="text-sm font-medium text-gray-900">{lead.deal_id}</span>
                </div>
              )}
              <div>
                <span className="text-xs text-gray-500 block">Name</span>
                <span className="text-sm font-medium text-gray-900">{lead.dm_full_name || lead.contact_person || 'Unknown'}</span>
              </div>
              <div>
                <span className="text-xs text-gray-500 block">Company</span>
                <span className="text-sm font-medium text-gray-900">{lead.company_name || 'Unknown Company'}</span>
              </div>
              <div>
                <span className="text-xs text-gray-500 block">Channel</span>
                <div className="mt-1">
                  {getChannelTag(lead.channel)}
                </div>
              </div>
            </div>
          </div>

          {/* Company Details */}
          <div>
            <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center">
              <Building size={16} className="mr-2 text-green-600" />
              Company Details
            </h4>
            <div className="space-y-3 bg-gray-50 p-3 rounded-lg">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <span className="text-xs text-gray-500 block">Company</span>
                  <span className="text-sm text-gray-900">{lead.company_name || 'Unknown'}</span>
                </div>
                <div>
                  <span className="text-xs text-gray-500 block">Country</span>
                  <span className="text-sm text-gray-900">{lead.country || 'Unknown'}</span>
                </div>
                <div>
                  <span className="text-xs text-gray-500 block">Employees</span>
                  <span className="text-sm text-gray-900">{lead.employee_headcount || 'Unknown'}</span>
                </div>
                <div>
                  <span className="text-xs text-gray-500 block">Type</span>
                  <span className="text-sm text-gray-900">{lead.company_type || 'Unknown'}</span>
                </div>
              </div>
              <div>
                <span className="text-xs text-gray-500 block">Primary Industry</span>
                <span className="text-sm text-gray-900">{lead.industry_1 || lead.industry || 'Unknown'}</span>
              </div>
              {lead.industry_2 && (
                <div>
                  <span className="text-xs text-gray-500 block">Secondary Industry</span>
                  <span className="text-sm text-gray-900">{lead.industry_2}</span>
                </div>
              )}
              {lead.products_services && (
                <div>
                  <span className="text-xs text-gray-500 block">Products & Services</span>
                  <span className="text-sm text-gray-900">{lead.products_services}</span>
                </div>
              )}
              <div className="flex items-center space-x-2 pt-2">
                {lead.company_website && (
                  <a 
                    href={lead.company_website.startsWith('http') ? lead.company_website : `https://${lead.company_website}`}
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-800 transition-colors text-xs"
                  >
                    🌐 Website
                  </a>
                )}
                {lead.company_linkedin && (
                  <a 
                    href={lead.company_linkedin.startsWith('http') ? lead.company_linkedin : `https://${lead.company_linkedin}`}
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-800 transition-colors text-xs"
                  >
                    <Linkedin size={12} className="inline mr-1" />
                    LinkedIn
                  </a>
                )}
                {lead.company_phone && (
                  <span className="text-gray-700 text-xs">
                    <Phone size={12} className="inline mr-1" />
                    {lead.company_phone}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Contact Details */}
          <div>
            <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center">
              <Phone size={16} className="mr-2 text-orange-600" />
              Contact Details
            </h4>
            <div className="space-y-3 bg-gray-50 p-3 rounded-lg">
              {(lead.dm_mobile_1 || lead.dm_mobile_2) && (
                <div>
                  <span className="text-xs text-gray-500 block mb-2">Mobile Numbers</span>
                  <div className="space-y-1">
                    {lead.dm_mobile_1 && (
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-900">{lead.dm_mobile_1}</span>
                        {lead.mobile_verified && <span className="text-green-600 text-xs">✅</span>}
                      </div>
                    )}
                    {lead.dm_mobile_2 && (
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-900">{lead.dm_mobile_2}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}
              
              {(lead.dm_email_1 || lead.dm_email_2) && (
                <div>
                  <span className="text-xs text-gray-500 block mb-2">Email Addresses</span>
                  <div className="space-y-1">
                    {lead.dm_email_1 && (
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-900">{lead.dm_email_1}</span>
                        {lead.email_verified && <span className="text-green-600 text-xs">✅</span>}
                      </div>
                    )}
                    {lead.dm_email_2 && (
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-900">{lead.dm_email_2}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}
              
              {lead.dm_linkedin && (
                <div>
                  <span className="text-xs text-gray-500 block mb-1">LinkedIn Profile</span>
                  <div className="flex items-center justify-between">
                    <a 
                      href={lead.dm_linkedin.startsWith('http') ? lead.dm_linkedin : `https://${lead.dm_linkedin}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-800 text-sm"
                    >
                      View Profile
                    </a>
                    {lead.linkedin_verified && <span className="text-green-600 text-xs">✅</span>}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Communication Status */}
          <div>
            <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center">
              <MessageSquare size={16} className="mr-2 text-cyan-600" />
              Communication Status
            </h4>
            <div className="space-y-3 bg-gray-50 p-3 rounded-lg">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <span className="text-xs text-gray-500 block">Lead Status</span>
                  <span className="text-sm text-gray-900">{lead.lead_status || lead.status || 'Unknown'}</span>
                </div>
                <div>
                  <span className="text-xs text-gray-500 block">Pipeline Stage</span>
                  <span className="text-sm text-gray-900">{lead.pipeline_stage || lead.stage || 'Unknown'}</span>
                </div>
              </div>
              
              {/* Last Activity */}
              <div>
                <span className="text-xs text-gray-500 block">Last Activity</span>
                <span className="text-sm text-gray-900">
                  {lastActivity || 'No recent activity'}
                </span>
                {lead.last_reply_timestamp && (
                  <div className="text-xs text-gray-400 mt-1">
                    {formatDateTime(lead.last_reply_timestamp)}
                  </div>
                )}
              </div>

              {/* Next Follow Up */}
              <div>
                <span className="text-xs text-gray-500 block">Next Follow Up</span>
                <span className="text-sm text-gray-900">
                  {nextFollowUp || 'No follow-up scheduled'}
                </span>
                {lead.next_fu_datetime && (
                  <div className="text-xs text-gray-400 mt-1">
                    {formatDateTime(lead.next_fu_datetime)}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Sales Person */}
          {lead.sales_person && (
            <div>
              <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center">
                <User size={16} className="mr-2 text-indigo-600" />
                Sales Person Assigned
              </h4>
              <div className="bg-purple-50 p-3 rounded-lg">
                <span className="text-sm font-medium text-purple-900">{lead.sales_person}</span>
              </div>
            </div>
          )}

          {/* Meeting Details */}
          {(lead.meeting_status || lead.date_booked || lead.meeting_date || lead.meeting_time) && (
            <div>
              <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center">
                <Calendar size={16} className="mr-2 text-pink-600" />
                Meeting Details
              </h4>
              <div className="space-y-3 bg-gray-50 p-3 rounded-lg">
                {lead.meeting_status && (
                  <div>
                    <span className="text-xs text-gray-500 block">Status</span>
                    <span className="text-sm text-gray-900">{lead.meeting_status}</span>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-3">
                  {lead.date_booked && (
                    <div>
                      <span className="text-xs text-gray-500 block">Booked When</span>
                      <span className="text-sm text-gray-900">
                        {new Date(lead.date_booked).toLocaleDateString()}
                      </span>
                    </div>
                  )}
                  {lead.meeting_date && (
                    <div>
                      <span className="text-xs text-gray-500 block">Meeting Date</span>
                      <span className="text-sm text-gray-900">
                        {new Date(lead.meeting_date).toLocaleDateString()}
                      </span>
                    </div>
                  )}
                </div>
                {lead.meeting_time && (
                  <div>
                    <span className="text-xs text-gray-500 block">Meeting Time</span>
                    <span className="text-sm text-gray-900">{lead.meeting_time}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Notes Section */}
          <div>
            <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center">
              <Edit size={16} className="mr-2 text-gray-600" />
              Notes
            </h4>
            <div className="space-y-3">
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Add notes about this lead..."
                className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none text-sm"
                rows={4}
              />
              <button
                onClick={handleSaveNotes}
                disabled={isUpdatingNotes}
                className="flex items-center justify-center space-x-2 w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 transition-colors text-sm font-medium disabled:bg-blue-400 disabled:cursor-not-allowed"
              >
                {isUpdatingNotes ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                ) : (
                  <Save size={14} />
                )}
                <span>{isUpdatingNotes ? 'Saving...' : 'Save Notes'}</span>
              </button>
            </div>
          </div>

          {/* Actions */}
          <div>
            <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center">
              <Edit size={16} className="mr-2 text-gray-600" />
              Actions
            </h4>
            <div className="space-y-3">
              {/* Change Stage */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-2">Change Stage</label>
                <select
                  value={selectedStage}
                  onChange={(e) => handleStageChange(e.target.value)}
                  disabled={isUpdatingStage}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
                >
                  <option value="">Select Stage</option>
                  {PIPELINE_STAGE_NAMES.map(stage => (
                    <option key={stage} value={stage}>{stage}</option>
                  ))}
                </select>
                {isUpdatingStage && (
                  <div className="flex items-center space-x-2 mt-2 text-xs text-blue-600">
                    <div className="w-3 h-3 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                    <span>Updating stage...</span>
                  </div>
                )}
              </div>

              {/* Push to CRM */}
              <button
                onClick={handlePushToCRM}
                className="flex items-center justify-center space-x-2 w-full bg-purple-600 text-white py-2 px-4 rounded-md hover:bg-purple-700 transition-colors text-sm font-medium"
              >
                <Upload size={14} />
                <span>Push to CRM</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LeadDetailsModal;